import React, { useMemo, useState } from "react";
import {
 CalendarDays,
 Star,
 Target,
 CircleDot,
 Award,
 ChevronDown,
 ChevronUp,
 ChevronRight,
 BarChart3,
} from "lucide-react";
import "./PlayerAllTimeTab.css"

function PlayerAllTimeTab({
  player,
  teamHistory = [],
  gameStats = [],
  allStats = [],
  onGameClick,
}) {
 const [showFullCareerLog, setShowFullCareerLog] = useState(false);
 const [openSeasonId, setOpenSeasonId] = useState(null);
  const [careerTrendStat, setCareerTrendStat] = useState("PPG");
 const visibleStats = useMemo(() => {
  return (allStats || [])
   .filter((stat) => !stat.hidden)
   .sort(
    (a, b) =>
     (a.order ?? a.display_order ?? 999) -
     (b.order ?? b.display_order ?? 999)
   );
 }, [allStats]);

 const teamHistoryBySltId = useMemo(() => {
  return (teamHistory || []).reduce((acc, entry) => {
   if (!entry?.slt_id) return acc;
   acc[Number(entry.slt_id)] = entry;
   return acc;
  }, {});
 }, [teamHistory]);

 const getStatTotalFromRows = (rows, abbreviation) => {
  return rows
   .filter((row) => row?.stat?.abbreviation === abbreviation)
   .reduce((sum, row) => sum + Number(row.value || 0), 0);
 };

 const getGameDateValue = (game) => {
  return game?.date || game?.game_dateTime || null;
 };

 const formatDate = (date) => {
  if (!date) return "TBD";

  const dateValue =
   typeof date === "string" && date.includes("T")
    ? new Date(date)
    : new Date(`${date}T00:00:00`);

  if (Number.isNaN(dateValue.getTime())) return "TBD";

  return dateValue.toLocaleDateString("en-US", {
   month: "short",
   day: "numeric",
   year: "numeric",
  });
 };

 const getMatchup = (game) => {
  if (!game) return "Opponent TBD";

  const homeTeam = game?.home_team?.team?.name || "Unknown";
  const awayTeam = game?.away_team?.team?.name || "Unknown";

  return `${homeTeam} vs ${awayTeam}`;
 };

 const formatTrendLabel = (seasonName) => {
  if (!seasonName) return "Season";

  const parts = String(seasonName).split(" ");
  if (parts.length < 2) return seasonName;

  const year = parts[0]?.slice(-2);
  const season = parts.slice(1).join(" ");

  return `${year} ${season}`;
 };

 const playerSeasons = useMemo(() => {
  const statRows = (gameStats || []).filter((row) => {
   return row?.stat_id && row?.roster?.seasons_leagues_teams_id;
  });

  const rowsBySltId = statRows.reduce((acc, row) => {
   const sltId = Number(row.roster.seasons_leagues_teams_id);
   if (!acc[sltId]) acc[sltId] = [];
   acc[sltId].push(row);
   return acc;
  }, {});

  return Object.entries(rowsBySltId)
   .map(([sltIdRaw, rows]) => {
    const sltId = Number(sltIdRaw);
    const historyEntry = teamHistoryBySltId[sltId];

    const rowsByGameId = rows.reduce((acc, row) => {
     if (!row?.game_id) return acc;
     if (!acc[row.game_id]) acc[row.game_id] = [];
     acc[row.game_id].push(row);
     return acc;
    }, {});

    const games = Object.entries(rowsByGameId)
     .map(([gameId, gameRows]) => {
      const game = gameRows[0]?.game || null;

      const pts = getStatTotalFromRows(gameRows, "PTS");
      const ftm = getStatTotalFromRows(gameRows, "FTM");
      const fta = getStatTotalFromRows(gameRows, "FTA");
      const threes = getStatTotalFromRows(gameRows, "3PM");

      const stats = visibleStats.map((stat) => ({
       abbreviation: stat.abbreviation,
       value: getStatTotalFromRows(gameRows, stat.abbreviation),
      }));

      return {
       id: `${sltId}-${gameId}`,
       gameId,
       date: getGameDateValue(game),
       displayDate: formatDate(getGameDateValue(game)),
       matchup: getMatchup(game),
       pts,
       ftm,
       fta,
       ft: fta ? `${((ftm / fta) * 100).toFixed(1)}%` : "0.0%",
       threes,
       stats,
       rawGame: game,
      };
     })
     .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

    const gp = games.length;
    const pts = games.reduce((sum, game) => sum + Number(game.pts || 0), 0);
    const ftm = games.reduce((sum, game) => sum + Number(game.ftm || 0), 0);
    const fta = games.reduce((sum, game) => sum + Number(game.fta || 0), 0);
    const threes = games.reduce(
     (sum, game) => sum + Number(game.threes || 0),
     0
    );

    const bestGame = games
     .slice()
     .sort((a, b) => Number(b.pts || 0) - Number(a.pts || 0))[0];

    return {
     id: String(sltId),
     sltId,
     season: historyEntry?.season?.name || "Unknown Season",
     team: historyEntry?.team?.name || "Unknown Team",
     league: historyEntry?.league?.name || "Unknown League",
     gp,
     ppg: gp ? (pts / gp).toFixed(1) : "0.0",
     pts,
     ftm,
     fta,
     ft: fta ? `${((ftm / fta) * 100).toFixed(1)}%` : "0.0%",
     threes,
     bestGame: bestGame ? `${bestGame.pts} pts` : "—",
     bestGameRaw: bestGame || null,
     games,
     latestDate: games[0]?.date || null,
     oldestDate: games[games.length - 1]?.date || null,
    };
   })
   .filter((season) => season.gp > 0)
   .sort((a, b) => new Date(b.latestDate || 0) - new Date(a.latestDate || 0));
 }, [gameStats, teamHistoryBySltId, visibleStats]);



 const careerTotals = useMemo(() => {
  const games = playerSeasons.flatMap((season) => season.games);

  const gp = games.length;
  const pts = games.reduce((sum, game) => sum + Number(game.pts || 0), 0);
  const ftm = games.reduce((sum, game) => sum + Number(game.ftm || 0), 0);
  const fta = games.reduce((sum, game) => sum + Number(game.fta || 0), 0);
  const threes = games.reduce(
   (sum, game) => sum + Number(game.threes || 0),
   0
  );

  const uniqueSeasonNames = new Set(
   playerSeasons.map((season) => season.season).filter(Boolean)
  );

  const uniqueTeamNames = new Set(
   playerSeasons.map((season) => season.team).filter(Boolean)
  );

  const bestGame = games
   .slice()
   .sort((a, b) => Number(b.pts || 0) - Number(a.pts || 0))[0];

  const bestFtSeason = playerSeasons
   .filter((season) => Number(season.fta || 0) > 0)
   .slice()
   .sort((a, b) => {
    const aFt = Number(a.ftm || 0) / Number(a.fta || 1);
    const bFt = Number(b.ftm || 0) / Number(b.fta || 1);
    return bFt - aFt;
   })[0];

  const mostActiveSeason = playerSeasons
   .slice()
   .sort((a, b) => Number(b.gp || 0) - Number(a.gp || 0))[0];

  return {
   gp,
   pts,
   ftm,
   fta,
   threes,
   ppg: gp ? (pts / gp).toFixed(1) : "0.0",
   ft: fta ? `${((ftm / fta) * 100).toFixed(1)}%` : "0.0%",
   seasons: uniqueSeasonNames.size,
   teams: uniqueTeamNames.size,
   bestGame,
   bestFtSeason,
   mostActiveSeason,
  };
 }, [playerSeasons]);

 const careerChartPoints = useMemo(() => {
  const groupedBySeason = playerSeasons.reduce((acc, season) => {
   const key = season.season || season.id;

   if (!acc[key]) {
    acc[key] = {
     label: season.season,
     gp: 0,
     pts: 0,
    threes: 0,
     latestDate: season.latestDate,
     oldestDate: season.oldestDate,
    };
   }

   acc[key].gp += Number(season.gp || 0);
   acc[key].pts += Number(season.pts || 0);
  acc[key].threes += Number(season.threes || 0);

   const currentLatest = new Date(acc[key].latestDate || 0);
   const nextLatest = new Date(season.latestDate || 0);

   if (nextLatest > currentLatest) {
    acc[key].latestDate = season.latestDate;
   }

   const currentOldest = new Date(acc[key].oldestDate || season.oldestDate || 0);
   const nextOldest = new Date(season.oldestDate || 0);

   if (!acc[key].oldestDate || nextOldest < currentOldest) {
    acc[key].oldestDate = season.oldestDate;
   }

   return acc;
  }, {});

  return Object.values(groupedBySeason)
   .map((season) => ({
    label: season.label,
     value:
       careerTrendStat === "3PM"
         ? Number(season.threes || 0)
         : season.gp
           ? Number((season.pts / season.gp).toFixed(1))
           : 0,
    sortDate: season.oldestDate || season.latestDate,
   }))
   .sort((a, b) => new Date(a.sortDate || 0) - new Date(b.sortDate || 0));
 }, [playerSeasons, careerTrendStat]);

 const chartMaxValue = Math.max(
  12,
  ...careerChartPoints.map((point) => Number(point.value) || 0)
 );

 const chartWidth = 560;
 const chartHeight = 210;
 const chartLeft = 70;
 const chartRight = 505;
 const chartTop = 25;
 const chartBottom = 145;
 const chartInnerWidth = chartRight - chartLeft;
 const chartInnerHeight = chartBottom - chartTop;

 const getChartX = (index) => {
  if (careerChartPoints.length <= 1) return chartLeft;
  return chartLeft + (index / (careerChartPoints.length - 1)) * chartInnerWidth;
 };

 const getChartY = (value) => {
  return chartBottom - ((Number(value) || 0) / chartMaxValue) * chartInnerHeight;
 };

 const polylinePoints = careerChartPoints
  .map((point, index) => `${getChartX(index)},${getChartY(point.value)}`)
  .join(" ");

 const toggleSeason = (seasonId) => {
  setOpenSeasonId((currentId) => (currentId === seasonId ? null : seasonId));
 };
 const hasStats = playerSeasons.length > 0;
 const hasMultipleSeasons = careerChartPoints.length > 1;
 const visiblePlayerSeasons = showFullCareerLog
  ? playerSeasons
  : playerSeasons.slice(0, 5);

 const hasMoreSeasons = playerSeasons.length > 5;
 return (
  <section className="player-alltime-grid">
   <div className="player-alltime-card player-alltime-snapshot-card">
    <div className="player-alltime-card-header">
     <h3>Career Snapshot</h3>

     <span>
      <CalendarDays size={14} /> All-Time
     </span>
    </div>

    <div className="player-alltime-snapshot-grid">
     <div className="player-alltime-snapshot-item">
      <span>Total Points</span>
      <strong>{careerTotals.pts}</strong>
     </div>

     <div className="player-alltime-snapshot-item">
      <span>Total 3PM</span>
      <strong>{careerTotals.threes}</strong>
     </div>
     <div className="player-alltime-snapshot-item">
      <span>Games Played</span>
      <strong>{careerTotals.gp}</strong>
     </div>
     <div className="player-alltime-snapshot-item">
      <span>Total FTM</span>
      <strong>{careerTotals.ftm}</strong>
     </div>

     <div className="player-alltime-snapshot-item">
      <span>Total FTA</span>
      <strong>{careerTotals.fta}</strong>
     </div>

     <div className="player-alltime-snapshot-item">
      <span>Seasons Played</span>
      <strong>{careerTotals.seasons}</strong>
     </div>


    </div>

    <small className="player-alltime-note">
     ● Career stats across all tracked games
    </small>
   </div>

   {hasMultipleSeasons ? (
    <div className="player-alltime-card player-alltime-trend-card">
     <div className="player-alltime-card-header">
      <h3>Season-by-Season Trend</h3>

           <select
             value={careerTrendStat}
             onChange={(e) => setCareerTrendStat(e.target.value)}
           >
             <option value="PPG">PPG</option>
             <option value="3PM">Total 3PM</option>
           </select>
     </div>

     <div className="player-alltime-chart-wrap">
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="player-alltime-line-chart">
       <line x1="25" y1="25" x2="540" y2="25" />
       <line x1="25" y1="65" x2="540" y2="65" />
       <line x1="25" y1="105" x2="540" y2="105" />
       <line x1="25" y1="145" x2="540" y2="145" />

       <polyline points={polylinePoints} />

       {careerChartPoints.map((point, index) => {
        const x = getChartX(index);
        const y = getChartY(point.value);

        return (
         <g key={`${point.label}-${index}`}>
          <circle cx={x} cy={y} r="4" />
          <text x={x} y={y - 12}>
           {point.value}
          </text>
            <text
              x={x}
              y={index % 2 === 0 ? "172" : "190"}
              className="player-alltime-chart-label"
            >
              {formatTrendLabel(point.label)}
            </text>
         </g>
        );
       })}
      </svg>

           <p>
             {careerTrendStat === "3PM"
               ? "Career 3PM by Season"
               : "Career PPG by Season"}
           </p>
     </div>
    </div>
   ) : (
    <div className="player-alltime-card player-alltime-trend-card">
     <div className="player-alltime-card-header">
      <h3>Career History</h3>
     </div>

     <div className="player-alltime-empty-state">
      <BarChart3 size={34} />
      <strong>Career history will build over time</strong>
      <p>
       Season-by-season trends will appear once this player has stats
       from multiple seasons.
      </p>
     </div>
    </div>
   )}

   <div className="player-alltime-card player-alltime-highlights-card">
    <h3>Career Highlights</h3>

    <div className="player-alltime-highlight-row">
     <Star />

     <div>
      <strong>Career High</strong>
      <p>
       {careerTotals.bestGame
        ? `${careerTotals.bestGame.pts} points on ${careerTotals.bestGame.displayDate}`
        : "No career high yet"}
      </p>
     </div>
    </div>

    <div className="player-alltime-highlight-row">
     <Target />

     <div>
      <strong>Best Free Throw Season</strong>
      <p>
       {careerTotals.bestFtSeason
        ? `${careerTotals.bestFtSeason.ft} from the line during ${careerTotals.bestFtSeason.season}`
        : "No free throw attempts recorded yet"}
      </p>
     </div>
    </div>

    <div className="player-alltime-highlight-row">
     <CircleDot />

     <div>
      <strong>Career 3PT Shooter</strong>
      <p>
       {careerTotals.threes} three-pointer
       {careerTotals.threes === 1 ? "" : "s"} made across{" "}
       {careerTotals.gp} game{careerTotals.gp === 1 ? "" : "s"}
      </p>
     </div>
    </div>

    <div className="player-alltime-highlight-row player-alltime-highlight-row-last">
     <Award />

     <div>
      <strong>Most Active Season</strong>
      <p>
       {careerTotals.mostActiveSeason
        ? `Played ${careerTotals.mostActiveSeason.gp} games during ${careerTotals.mostActiveSeason.season}`
        : "No season activity yet"}
      </p>
     </div>
    </div>
   </div>

   {hasStats ? (
    <div className="player-alltime-card player-alltime-season-breakdown-card">
     <div className="player-alltime-card-header">
      <h3>Season Breakdown</h3>

     </div>

     <div className="player-alltime-table-wrap">
      <table>
       <thead>
        <tr>
         <th>Season</th>
         <th>Team</th>
         <th>League</th>
         <th>GP</th>
         <th>PPG</th>
         <th>PTS</th>
         <th>FT%</th>
         <th>3PM</th>

        </tr>
       </thead>

       <tbody>
        {visiblePlayerSeasons.map((season) => (
         <React.Fragment key={season.id}>
          <tr
           className={`player-alltime-season-summary-row ${openSeasonId === season.id ? "open" : ""
            }`}
           onClick={() => toggleSeason(season.id)}
          >
           <td>
            <button className="player-alltime-season-toggle" type="button">
             {openSeasonId === season.id ? (
              <ChevronUp size={16} />
             ) : (
              <ChevronDown size={16} />
             )}

             {season.season}
            </button>
           </td>

           <td>{season.team}</td>
           <td>{season.league}</td>
           <td>{season.gp}</td>
           <td>{season.ppg}</td>
           <td>{season.pts}</td>
           <td>{season.ft}</td>
           <td>{season.threes}</td>
          </tr>

          {openSeasonId === season.id && (
           <tr className="player-alltime-season-games-row">
            <td colSpan="8">
             <div className="player-alltime-season-games-panel">
              <div className="player-alltime-inner-table-wrap">
               <table>
                <thead>
                 <tr>
                  <th>Date</th>
                  <th>Matchup</th>
                  <th>PTS</th>
                  <th>FTM</th>
                  <th>FTA</th>
                  <th>FT%</th>
                  <th>3PM</th>
                 </tr>
                </thead>

                <tbody>
                 {season.games.map((game) => (
                   <tr
                     key={game.id}
                     className="player-alltime-clickable-game-row"
                     onClick={() => {
                       if (!game.rawGame?.id) return;
                       onGameClick?.(game.rawGame.id);
                     }}
                   >
                   <td>{game.displayDate}</td>
                   <td>{game.matchup}</td>
                   <td>{game.pts}</td>
                   <td>{game.ftm}</td>
                   <td>{game.fta}</td>
                   <td>{game.ft}</td>
                   <td>{game.threes}</td>
                  </tr>
                 ))}
                </tbody>
               </table>
              </div>
             </div>
            </td>
           </tr>
          )}
         </React.Fragment>
        ))}
       </tbody>
      </table>
      {hasMoreSeasons && (
       <div className="player-alltime-show-more-row">
        <button
         type="button"
         onClick={() => {
          setShowFullCareerLog((current) => !current);
          setOpenSeasonId(null);
         }}
        >
         {showFullCareerLog ? "Show Less" : "View Full Career Game Log"}
         <ChevronRight size={15} />
        </button>
       </div>
      )}
     </div>
    </div>
   ) : (
    <div className="player-alltime-card player-alltime-season-breakdown-card">
     <div className="player-alltime-card-header">
      <h3>Season Breakdown</h3>
     </div>

     <div className="player-alltime-empty-state">
      <CalendarDays size={34} />
      <strong>No career stats yet</strong>
      <p>
       This player’s all-time stats will appear once game results are
       added.
      </p>
     </div>
    </div>
   )}
  </section>
 );
}

export default PlayerAllTimeTab;